"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.opRegisterExportQuerySchema = exports.opRegisterExportFormatSchema = exports.opRegisterReconciliationQuerySchema = exports.opRegisterSummaryQuerySchema = exports.opRegisterQuerySchema = exports.opRegisterFiltersSchema = exports.opRegisterSortFieldSchema = exports.opRegisterGroupBySchema = exports.opRegisterPaymentModeSchema = exports.opRegisterPayerClassSchema = exports.opRegisterBillStatusSchema = exports.opRegisterGrainSchema = void 0;
/**
 * OP Billing Register — request contracts. ADR-084.
 *
 * THE MOST IMPORTANT THING IN THIS FILE IS WHAT IT REFUSES TO ACCEPT.
 *
 * `billStatus` below admits only OPEN / PARTIALLY_PAID / PAID / OVERPAID /
 * WRITTEN_OFF. `CANCELLED` and `REVERSED` are ABSENT, deliberately and
 * permanently (ADR-084 §1). The register is the official OP revenue register;
 * a cancelled bill is not revenue and a reversed one has had its posted effect
 * negated by a linked reversing entry. Neither may ever appear, so neither is
 * expressible as a request.
 *
 * That is the FIRST of two independent mechanisms. The second is the server's
 * own posted-only fence, composed after every user filter and unable to be
 * overridden. Two, because one is a single typo away from being lost, and the
 * failure mode — a cancelled bill silently counted as revenue — is the kind
 * that is discovered by an auditor rather than by a test.
 *
 * NO AMOUNT IS EVER AN INPUT here either, for the same reason the billing
 * request contracts say so: the client names what to FILTER, never what
 * anything cost.
 */
const zod_1 = require("zod");
const uuid = zod_1.z.string().uuid();
/**
 * BILL: one row per posted bill. SERVICE: one row per posted invoice line.
 *
 * Two grains rather than one flat grid, because the requested column set mixes
 * bill-level facts (payment split, cashier, round-off) with line-level ones
 * (service code, qty, unit rate). Flattened together, bill-level money repeats
 * on every service line and any column sum double-counts — see ADR-084 §2 for
 * why that is the single most likely way this report states a wrong number.
 */
exports.opRegisterGrainSchema = zod_1.z.enum(['BILL', 'SERVICE']);
/**
 * Narrowing WITHIN the posted set only. See the file header for why CANCELLED
 * and REVERSED are not options.
 */
exports.opRegisterBillStatusSchema = zod_1.z.enum([
    'OPEN',
    'PARTIALLY_PAID',
    'PAID',
    'OVERPAID',
    'WRITTEN_OFF',
]);
/**
 * Corporate / CGHS / Insurance are values of ONE axis, not three booleans
 * (ADR-084 §4). CGHS in particular is not a schema concept — it is a
 * `PayerMaster` of type `GOVERNMENT_SCHEME`. Presenting three independent
 * flags would imply data this database does not hold.
 */
exports.opRegisterPayerClassSchema = zod_1.z.enum([
    'SELF_PAY',
    'INSURANCE',
    'TPA',
    'CORPORATE',
    'GOVERNMENT_SCHEME',
]);
/** Cash / Card / UPI / Cheque / Bank transfer, plus a named bucket for the rest. */
exports.opRegisterPaymentModeSchema = zod_1.z.enum([
    'CASH',
    'CARD',
    'UPI',
    'CHEQUE',
    'BANK_TRANSFER',
    'OTHER',
]);
/**
 * Grouping axes. Allowlisted for the same reason `sortBy` is: an arbitrary
 * group key is both an injection surface and a guaranteed table scan.
 *
 * `SERVICE`, `ACCOUNT_HEAD` and `REVENUE_SUB_HEAD` are line-level and therefore
 * valid only in SERVICE grain — the server rejects the combination rather than
 * silently returning bill-grain rows grouped by a column they do not carry.
 */
exports.opRegisterGroupBySchema = zod_1.z.enum([
    'NONE',
    'DOCTOR',
    'DEPARTMENT',
    'ACCOUNT_HEAD',
    'REVENUE_SUB_HEAD',
    'SERVICE',
    'PAYMENT_MODE',
    'PAYER_CLASS',
    'CASHIER',
    'COUNTER',
    'DATE',
]);
/** Allowlisted — anything else is a 400 rather than a slow success. */
exports.opRegisterSortFieldSchema = zod_1.z.enum([
    'invoiceDate',
    'invoiceNumber',
    'netAmount',
    'patientName',
]);
/**
 * The filter set. Every field maps to an indexed column or to a snapshot column
 * on the line — never to a join against a live master, because a master join
 * would let a rename rewrite a historical figure (ADR-083, ADR-065).
 *
 * Text filters (`patientName`, `serviceName`) are prefix-matched
 * case-insensitively. Deliberately NOT `contains`: a leading wildcard cannot
 * use an index, and this table is specified to hold 100,000+ bills.
 */
exports.opRegisterFiltersSchema = zod_1.z.object({
    // --- period -------------------------------------------------------------
    fromDate: zod_1.z.string().date().optional(),
    toDate: zod_1.z.string().date().optional(),
    // --- document -----------------------------------------------------------
    billNumber: zod_1.z.string().trim().min(1).max(30).optional(),
    billStatus: exports.opRegisterBillStatusSchema.optional(),
    // --- patient ------------------------------------------------------------
    patientId: uuid.optional(),
    uhid: zod_1.z.string().trim().min(1).max(30).optional(),
    patientName: zod_1.z.string().trim().min(1).max(120).optional(),
    mobile: zod_1.z.string().trim().min(3).max(20).optional(),
    /**
     * TRUE = walk-ins only — the Private Patient Register (desk, 2026-09-25).
     * FALSE = patient-identified bills only. Absent = both, today's behaviour.
     */
    isWalkIn: zod_1.z.coerce.boolean().optional(),
    /** Exact match on the allotted Private Patient Register number. See `FinInvoice.walkInNumber`. */
    walkInNumber: zod_1.z.string().trim().min(1).max(30).optional(),
    // --- clinical / organisational -----------------------------------------
    doctorId: uuid.optional(),
    departmentId: uuid.optional(),
    /**
     * The line's SNAPSHOTTED service department, not the bill header's
     * department. They differ whenever a bill spans departments, which is the
     * normal case for a multi-service OP bill — and the snapshot is the one that
     * reconciles with the revenue reports.
     */
    serviceDepartment: zod_1.z.string().trim().min(1).max(255).optional(),
    // --- ADR-083 classification (all snapshot columns) ----------------------
    accountHeadId: uuid.optional(),
    revenueSubHead: zod_1.z.string().trim().min(1).max(150).optional(),
    /** `SERV CODE` + `SERV NUMBER` as billed, e.g. `BCA001`. */
    serviceCode: zod_1.z.string().trim().min(1).max(30).optional(),
    serviceName: zod_1.z.string().trim().min(1).max(255).optional(),
    // --- counter / cash -----------------------------------------------------
    cashierUserId: uuid.optional(),
    counterId: uuid.optional(),
    /**
     * "Shift" in the brief. This schema has no shift entity — a `CashSession`
     * IS the shift, bounded by `openingAt`/`closingAt` (ADR-084 §5).
     */
    cashSessionId: uuid.optional(),
    // --- commercial ---------------------------------------------------------
    paymentMode: exports.opRegisterPaymentModeSchema.optional(),
    payerId: uuid.optional(),
    payerClass: exports.opRegisterPayerClassSchema.optional(),
    /** TRUE = lines that carried a non-zero GST rate; FALSE = zero-rated/exempt. */
    gstApplicable: zod_1.z.coerce.boolean().optional(),
    // --- audit --------------------------------------------------------------
    createdBy: uuid.optional(),
    modifiedBy: uuid.optional(),
});
/**
 * A page of the register.
 *
 * Cursor, not offset. The underlying table takes concurrent inserts all day,
 * and offset paging under concurrent inserts both duplicates and skips rows
 * mid-page — a register that shows an operator the same bill twice is worse
 * than one that is slow.
 */
exports.opRegisterQuerySchema = exports.opRegisterFiltersSchema.extend({
    grain: exports.opRegisterGrainSchema.default('BILL'),
    groupBy: exports.opRegisterGroupBySchema.default('NONE'),
    sortBy: exports.opRegisterSortFieldSchema.default('invoiceDate'),
    sortDir: zod_1.z.enum(['asc', 'desc']).default('desc'),
    limit: zod_1.z.coerce.number().int().positive().max(200).default(50),
    /** Opaque, composite `(sortValue, id)`. Never parsed by the client. */
    cursor: zod_1.z.string().min(1).max(512).optional(),
});
/** Summary cards and grand totals cover the WHOLE filtered set, so they take no cursor. */
exports.opRegisterSummaryQuerySchema = exports.opRegisterFiltersSchema.extend({
    grain: exports.opRegisterGrainSchema.default('BILL'),
});
/** The reconciliation banner reads the same filtered set the grid does. */
exports.opRegisterReconciliationQuerySchema = exports.opRegisterFiltersSchema;
exports.opRegisterExportFormatSchema = zod_1.z.enum(['csv', 'xlsx', 'pdf']);
/**
 * Export carries no cursor: an export is of the whole filtered set by
 * definition. `maxRows` is a hard ceiling rather than a page size — an export
 * that silently truncated would be the worst possible failure for a document
 * whose entire purpose is to be complete, so the response states whether the
 * ceiling was hit and the server refuses rather than truncating quietly.
 */
exports.opRegisterExportQuerySchema = exports.opRegisterFiltersSchema.extend({
    grain: exports.opRegisterGrainSchema.default('BILL'),
    sortBy: exports.opRegisterSortFieldSchema.default('invoiceDate'),
    sortDir: zod_1.z.enum(['asc', 'desc']).default('desc'),
    format: exports.opRegisterExportFormatSchema,
});
//# sourceMappingURL=op-billing-register.request.js.map